"use client";

import * as React from "react";

import type { FetchApiError, StrapiCollectionResponse, StrapiEntity, StrapiQuery } from "@/lib/strapi";
import { FetchApiError as FetchApiErrorClass, fetchStrapi } from "@/lib/strapi";
import type { ProductReviewAttributes } from "@/services/productReview";

export type UseAllProductReviewsOptions = {
  enabled?: boolean;
  query?: StrapiQuery;
  pageSize?: number;
  withBrandFilter?: boolean;
  path?: string;
};

export function useAllProductReviews(options?: UseAllProductReviewsOptions) {
  const enabled = options?.enabled ?? true;
  const pageSize = options?.pageSize ?? 100;

  const [data, setData] = React.useState<StrapiCollectionResponse<ProductReviewAttributes> | null>(null);
  const [error, setError] = React.useState<FetchApiError | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [refetchIndex, setRefetchIndex] = React.useState(0);

  const refetch = React.useCallback(() => {
    setRefetchIndex((prev) => prev + 1);
  }, []);

  React.useEffect(() => {
    if (!enabled) return;

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      if (abortController.signal.aborted) return;

      setIsLoading(true);
      setError(null);

      const baseQuery: StrapiQuery = {
        ...(options?.query ?? {}),
        sort: options?.query?.sort ?? ["updatedAt:desc"],
      };

      const fetchPage = (page: number) =>
        fetchStrapi<StrapiCollectionResponse<ProductReviewAttributes>>(options?.path ?? "/product-reviews", {
          query: {
            ...baseQuery,
            pagination: { page, pageSize },
          },
          withBrandFilter: options?.withBrandFilter ?? true,
          init: { signal: abortController.signal },
        });

      fetchPage(1)
        .then(async (first) => {
          if (abortController.signal.aborted) return;

          const pagination = first.meta?.pagination;
          const pageCount = typeof pagination?.pageCount === "number" ? pagination.pageCount : 1;

          if (pageCount <= 1) {
            setData(first);
            return;
          }

          const restPages = Array.from({ length: pageCount - 1 }, (_, idx) => idx + 2);
          const rest = await Promise.all(restPages.map((p) => fetchPage(p)));
          if (abortController.signal.aborted) return;

          const mergedData: Array<StrapiEntity<ProductReviewAttributes>> = [
            ...(first.data ?? []),
            ...rest.flatMap((r) => r.data ?? []),
          ];

          setData({
            ...first,
            data: mergedData,
            meta: {
              ...first.meta,
              pagination: pagination
                ? { ...pagination, page: 1, pageSize: mergedData.length, pageCount: 1 }
                : first.meta.pagination,
            },
          });
        })
        .catch((err: unknown) => {
          if (abortController.signal.aborted) return;
          const normalized =
            err instanceof FetchApiErrorClass ? err : new FetchApiErrorClass("Request failed", { cause: err });
          setError(normalized);
          setData(null);
        })
        .finally(() => {
          if (abortController.signal.aborted) return;
          setIsLoading(false);
        });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [enabled, options?.path, options?.query, options?.withBrandFilter, pageSize, refetchIndex]);

  const reviews: Array<StrapiEntity<ProductReviewAttributes>> = data?.data ?? [];

  return { data, error, isLoading, refetch, reviews };
}

